import { createAsyncThunk } from '@reduxjs/toolkit';
import PostAPI from './postAPI';

// Posts
export const fetchPostsWithPictures = createAsyncThunk(
  'posts/fetchWithPictures',
  async (_, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchAll();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createPost = createAsyncThunk(
  'posts/create',
  async ({ newPostData }, { rejectWithValue }) => {
    try {
      return await PostAPI.create(newPostData);
    } catch (err) {
      // Backend wraps error details as {error: {message}}, not {detail} --
      // reading err.message alone lost the real reason (e.g. the
      // verify-your-email gate's message) and fell back to a generic one.
      return rejectWithValue(err.response?.data?.error?.message || err.message || 'Failed to create post');
    }
  }
);

export const updatePost = createAsyncThunk(
  'posts/update',
  async ({ postId, updatedPostData }, { rejectWithValue }) => {
    try {
      return await PostAPI.update(postId, updatedPostData);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const loadMorePosts = createAsyncThunk(
  'posts/loadMore',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().posts || {};
      const { posts = [], currentFilters = {}, isFiltered = false } = state;
      const skip = posts.length || 0;

      if (isFiltered) {
        const {
          limit = 10,
          sort_by = 'latest',
          period = 'all',
          sentiment_filter = null,
          stock_symbol = null,
        } = currentFilters;

        // Use the filter-aware API (auto-switches to /posts/stock/symbol/:symbol when stock_symbol exists)
        const { data, filters } = await PostAPI.fetchWithFilters({
          skip,
          limit,
          sort_by,
          period,
          sentiment_filter,
          stock_symbol,
          replace: false,
        });

        const pagePosts = data?.posts ?? data ?? [];
        const hasMore =
          typeof data?.has_more === 'boolean'
            ? data.has_more
            : (Array.isArray(pagePosts) && pagePosts.length >= (limit ?? 10));

        return {
          posts: pagePosts,
          hasMore,
          meta: {
            current_page: data?.current_page,
            per_page: data?.per_page ?? limit,
            sort_by: data?.sort_by ?? sort_by,
            period: data?.period ?? period,
          },
          replace: false,
          _filtersEcho: filters, // optional (safe to keep or remove)
        };
      }

      // Unfiltered pagination
      const resp = await PostAPI.loadMore(skip);
      return { ...resp, replace: false };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);


// Filtered fetch
export const fetchPostsWithFilters = createAsyncThunk(
  'posts/fetchWithFilters',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data, filters } = await PostAPI.fetchWithFilters(params);
      return { data, filters, replace: params.replace !== false }; // default replace=true
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Load more (filtered)
export const loadMorePostsWithFilters = createAsyncThunk(
  'posts/loadMoreWithFilters',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { posts, currentFilters } = getState().posts;
      const skip = posts.length;
      const { data, filters } = await PostAPI.fetchWithFilters({
        ...currentFilters,
        skip,
        replace: false
      });
      return { data, filters, replace: false };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const likePost = createAsyncThunk(
  'posts/like',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.like(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const unlikePost = createAsyncThunk(
  'posts/unlike',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.unlike(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchPostsBySentiment = createAsyncThunk(
  'posts/fetchBySentiment',
  async ({ sentimentType, skip, limit }, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchBySentiment(sentimentType, skip, limit);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchUserPosts = createAsyncThunk(
  'posts/fetchUserPosts',
  async ({ userId, skip, limit }, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchUserPosts(userId, skip, limit);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deletePost = createAsyncThunk(
  'posts/delete',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.delete(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const bullPost = createAsyncThunk(
  'posts/bull',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.bull(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const removeBullVote = createAsyncThunk(
  'posts/removeBull',
  async ({ postId }, { rejectWithValue }) => {
    try {
      // uses DELETE /posts/:id/bull in PostAPI.unlikebull
      return await PostAPI.unlikebull(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const bearPost = createAsyncThunk(
  'posts/bear',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.bear(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const removeBearVote = createAsyncThunk(
  'posts/removeBear',
  async ({ postId }, { rejectWithValue }) => {
    try {
      // uses DELETE /posts/:id/bear in PostAPI.unlikebear
      return await PostAPI.unlikebear(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchPostById = createAsyncThunk(
  'posts/fetchById',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchById(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Stocks related to posts
export const fetchStocksForPost = createAsyncThunk(
  'posts/fetchStocksForPost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchStocksForPost(postId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updatePostStocks = createAsyncThunk(
  'posts/updateStocks',
  async ({ postId, stockIds }, { rejectWithValue }) => {
    try {
      return await PostAPI.updateStocks(postId, stockIds);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchFollowingPostsThunk = createAsyncThunk(
  'posts/fetchFollowing',
  async ({ skip = 0, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchFollowingPosts(skip, limit);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchUserHistoryPosts = createAsyncThunk(
  'posts/fetchUserHistoryPosts',
  async ({ userId, skip = 0, limit = 10, sort_by = 'latest', period = 'all' }, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchUserHistoryPosts(userId, { skip, limit, sort_by, period });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const loadMoreUserHistoryPosts = createAsyncThunk(
  'posts/loadMoreUserHistoryPosts',
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      const { userHistory } = getState().posts || {};
      const skip = (userHistory?.posts?.length || 0);
      const limit = userHistory?.perPage || 10;
      const sort_by = userHistory?.sortBy || 'latest';
      const period = userHistory?.period || 'all';
      return await PostAPI.fetchUserHistoryPosts(userId, { skip, limit, sort_by, period });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// GroupPost is a separate table from Post -- this is the only way to get a
// user's group posts into the same "Post History" view as their regular ones.
export const fetchUserGroupHistoryPosts = createAsyncThunk(
  'posts/fetchUserGroupHistoryPosts',
  async ({ userId, skip = 0, limit = 20 }, { rejectWithValue }) => {
    try {
      return await PostAPI.fetchUserGroupHistoryPosts(userId, { skip, limit });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const loadMoreUserGroupHistoryPosts = createAsyncThunk(
  'posts/loadMoreUserGroupHistoryPosts',
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      const { userGroupHistory } = getState().posts || {};
      const skip = userGroupHistory?.posts?.length || 0;
      return await PostAPI.fetchUserGroupHistoryPosts(userId, { skip, limit: 20 });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);